import { num } from "@/lib/format";
import { Gamepad2, Battery, Navigation, VideoOff } from "lucide-react";

// Cockpit HUD — RÉEL UNIQUEMENT. Fond = vidéo réelle (videoNode) si disponible, sinon noir neutre
// (jamais de paysage/flux simulé). Horizon artificiel seulement à partir de données réelles disponibles.
export default function CockpitVideoHUD({ droneId, padConnected, videoNode, videoActive, statusOverlay, realHud = null }) {
  const rh = realHud || {};

  return (
    <div data-testid="cockpit-video-hud" className="absolute inset-0 bg-black overflow-hidden select-none">
      {/* Fond : vidéo réelle si disponible, sinon noir neutre (aucun fond simulé) */}
      {videoNode ? (
        <div className="absolute inset-0" data-testid="cockpit-video-bg">{videoNode}</div>
      ) : (
        <div className="absolute inset-0 bg-black" data-testid="cockpit-video-none" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/55 pointer-events-none" />
      <div className="absolute inset-x-0 h-px bg-[#2DD4BF]/40 pointer-events-none" style={{ animation: "scan 5s linear infinite" }} />

      {/* Bandeau état caméra réel */}
      {videoActive ? (
        <div data-testid="cockpit-live-banner" className="absolute top-0 inset-x-0 flex items-center justify-center gap-2 bg-[#22C55E]/15 border-b border-[#22C55E]/40 py-1 z-20">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#22C55E]">Flux vidéo réel — {droneId}</span>
        </div>
      ) : (
        <div data-testid="cockpit-camera-unavailable" className="absolute top-0 inset-x-0 flex items-center justify-center gap-2 bg-black/60 border-b border-white/15 py-1 z-20">
          <VideoOff size={11} className="text-[#A3A3A3]" />
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A3A3A3]">Caméra indisponible — aucun flux réel</span>
        </div>
      )}

      {/* Overlay d'état vidéo (démarrage / indisponible / activer) */}
      {statusOverlay}

      {/* Horizon artificiel : seulement si pitch/roll réels disponibles et pas de vidéo active */}
      {!videoActive && rh.pitch != null && rh.roll != null && <ArtificialHorizon pitch={rh.pitch} roll={rh.roll} />}

      {/* Top-left readouts (réels) */}
      <div className="absolute top-7 left-3 font-mono text-[10px] space-y-0.5 z-10">
        <div className="font-chivo text-base font-black tracking-tighter text-white drop-shadow">{droneId}</div>
        <Badge color="#EF4444" label={`MODE: ${rh.mode || "—"}`} testid="hud-mode" />
        <div className="flex items-center gap-2 pt-0.5 text-[#A3A3A3]">
          <span className="flex items-center gap-1"><Battery size={10} style={{ color: (rh.battery ?? 100) < 25 ? "#EF4444" : "#22C55E" }} /> {rh.battery == null ? "—" : `${num(rh.battery, 0)}%`}</span>
          <span>GPS {rh.gps ? "FIX" : "—"}</span>
        </div>
      </div>

      {/* Top-right cluster (réel) */}
      <div className="absolute top-7 right-3 flex flex-col items-end gap-1 font-mono text-[10px] z-10">
        <span data-testid="hud-pad" className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${padConnected ? "border-[#22C55E]/50 text-[#22C55E]" : "border-white/15 text-[#A3A3A3]"}`}>
          <Gamepad2 size={11} /> {padConnected ? "Manette connectée" : "Manette non connectée"}
        </span>
        <span data-testid="hud-real-badge" className="px-1.5 py-0.5 rounded-sm border border-[#EF4444]/50 text-[#EF4444] tracking-wider">CONTRÔLE RÉEL</span>
      </div>

      {/* Tapes */}
      <Tape side="left" label="ALT" value={rh.alt == null ? "—" : num(rh.alt, 1)} unit="m" />
      <Tape side="right" label="SPD" value={rh.speed == null ? "—" : num(rh.speed, 1)} unit="m/s" />

      {/* Center reticle */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-10 h-10 border border-[#2DD4BF]/60 rounded-full flex items-center justify-center">
          <div className="w-1 h-1 bg-[#2DD4BF] rounded-full" />
        </div>
      </div>

      {/* Bottom data strip (réel) */}
      <div className="absolute bottom-1.5 inset-x-0 px-3 z-10 pointer-events-none">
        <Compass heading={rh.heading} />
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 font-mono text-[9px] text-[#A3A3A3]">
          <span data-testid="hud-pitch">PITCH {rh.pitch == null ? "—" : `${num(rh.pitch, 0)}°`}</span>
          <span data-testid="hud-roll">ROLL {rh.roll == null ? "—" : `${num(rh.roll, 0)}°`}</span>
          <span data-testid="hud-yaw">YAW {rh.yaw == null ? "—" : `${num(rh.yaw, 0)}°`}</span>
          <span data-testid="hud-coords">{rh.lat == null || rh.lng == null ? "—" : `${num(rh.lat, 5)}, ${num(rh.lng, 5)}`}</span>
        </div>
      </div>
    </div>
  );
}

function Badge({ color, label, testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border font-mono text-[9px] tracking-wider uppercase" style={{ color, borderColor: `${color}66` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} /> {label}
    </span>
  );
}

function ArtificialHorizon({ pitch, roll }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      <div className="w-[140%] h-[140%] relative" style={{ transform: `rotate(${-roll}deg) translateY(${pitch * 2}px)` }}>
        <div className="absolute left-0 right-0 top-1/2 h-px bg-[#2DD4BF]/50" />
        {[-20, -10, 10, 20].map((p) => (
          <div key={p} className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2" style={{ top: `calc(50% + ${p * 1.2}%)` }}>
            <span className="font-mono text-[8px] text-[#2DD4BF]/50">{Math.abs(p)}</span>
            <div className="w-16 h-px bg-[#2DD4BF]/25" />
            <span className="font-mono text-[8px] text-[#2DD4BF]/50">{Math.abs(p)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tape({ side, label, value, unit }) {
  return (
    <div className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-3" : "right-3"} bg-black/40 border border-[#2DD4BF]/30 rounded-sm px-2 py-1 backdrop-blur-sm z-10`}>
      <div className="font-mono text-[8px] tracking-[0.2em] text-[#2DD4BF]/70">{label}</div>
      <div className="font-mono text-base font-bold text-white leading-none">{value}</div>
      <div className="font-mono text-[8px] text-[#A3A3A3]">{unit}</div>
    </div>
  );
}

function Compass({ heading }) {
  const dirs = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  const known = heading != null && Number.isFinite(heading);
  const h = known ? heading : 0;
  const idx = Math.round(h / 45) % 8;
  return (
    <div className="flex items-center justify-center gap-1 font-mono text-[10px]">
      <Navigation size={11} className="text-[#2DD4BF]" style={{ transform: `rotate(${h}deg)` }} />
      <span className="text-white font-bold">{known ? dirs[idx] : "—"}</span>
      <span className="text-[#A3A3A3]">{known ? `${num(h, 0)}°` : "—"}</span>
    </div>
  );
}
